import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { PageHeader } from "../../components/Layout";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, ToggleLeft, ToggleRight, MagnifyingGlass, Clock, ListChecks, CheckSquare, Square, Coin, Buildings, Tag } from "@phosphor-icons/react";
import { useExamCategories } from "../../lib/examCategories";

const STATUS_TONE = {
  published: "border-emerald-300 text-emerald-700 bg-emerald-50",
  draft: "border-zinc-300 text-zinc-600 bg-zinc-50",
  archived: "border-amber-300 text-amber-800 bg-amber-50",
};

export default function ExamsManageList() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [instFilter, setInstFilter] = useState("all");
  const [institutions, setInstitutions] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkConfirm, setBulkConfirm] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [recatOpen, setRecatOpen] = useState(false);
  const [recatCategory, setRecatCategory] = useState("");
  const { categories: examCategories, labelFor, reload: reloadCategories } = useExamCategories();

  const isSuper = me?.role === "super_admin";
  const isAdmin = me?.role === "institution_admin";

  const load = () => {
    setLoading(true);
    api.get("/exams").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    if (isSuper) {
      api.get("/institutions").then((r) => setInstitutions(r.data)).catch(() => {});
    }
  }, [isSuper]);

  const instMap = useMemo(() => {
    const m = {};
    for (const i of institutions) m[i.institution_id] = i;
    return m;
  }, [institutions]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((e) => {
      if (statusFilter !== "all" && e.status !== statusFilter) return false;
      if (categoryFilter !== "all" && e.category !== categoryFilter) return false;
      if (isSuper && instFilter !== "all" && e.institution_id !== instFilter) return false;
      if (!q) return true;
      return (e.title || "").toLowerCase().includes(q) || (e.description || "").toLowerCase().includes(q);
    });
  }, [items, search, statusFilter, categoryFilter, instFilter, isSuper]);

  const stats = useMemo(() => ({
    total: items.length,
    published: items.filter((e) => e.status === "published").length,
    draft: items.filter((e) => e.status === "draft").length,
    archived: items.filter((e) => e.status === "archived").length,
  }), [items]);

  const togglePublish = async (e) => {
    setBusyId(e.exam_id);
    const nextStatus = e.status === "published" ? "draft" : "published";
    try {
      await api.put(`/exams/${e.exam_id}`, { ...e, status: nextStatus });
      toast.success(nextStatus === "published" ? "Exam enabled (published)" : "Exam disabled (draft)");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setBusyId(null);
    }
  };

  const doDelete = async (e) => {
    setBusyId(e.exam_id);
    try {
      const r = await api.delete(`/exams/${e.exam_id}`);
      if (r.data?.soft_deleted) toast.success(r.data.message || "Exam archived");
      else toast.success("Exam deleted");
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setBusyId(null);
    }
  };

  const toggleSel = (eid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(eid)) next.delete(eid); else next.add(eid);
      return next;
    });
  };
  const allIds = filtered.map((e) => e.exam_id);
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));
  const toggleSelectAll = () => setSelected(allSelected ? new Set() : new Set(allIds));
  const clearSelection = () => setSelected(new Set());

  const runBulk = async (action) => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/exams/bulk-action", { exam_ids: Array.from(selected), action });
      const skipped = r.data.skipped?.length || 0;
      const msg =
        action === "delete"
          ? `${r.data.hard_deleted} deleted, ${r.data.soft_deleted} archived${skipped ? `, ${skipped} skipped` : ""}`
          : `${r.data.processed} ${action}ed${skipped ? `, ${skipped} skipped` : ""}`;
      toast.success(msg);
      clearSelection();
      setBulkConfirm(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk action failed"); }
    finally { setBulkBusy(false); }
  };

  const runRecategorise = async () => {
    if (selected.size === 0 || !recatCategory) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/exams/bulk-recategorize", {
        exam_ids: Array.from(selected),
        category: recatCategory,
      });
      const sk = r.data.skipped?.length || 0;
      toast.success(`${r.data.processed} exam(s) moved to "${labelFor(recatCategory)}"${sk ? `, ${sk} skipped` : ""}`);
      clearSelection();
      setRecatOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Recategorise failed");
    } finally { setBulkBusy(false); }
  };

  return (
    <div>
      <PageHeader
        overline="Exam Builder"
        title="All exams"
        subtitle={isSuper
          ? "Global view — you can see and manage exams across every institution."
          : "Create, edit, publish, disable, or remove assessments. Only exams from your institution are shown."}
        action={
          <Link to="/app/exams-manage/new" className="bg-zinc-900 text-white px-4 py-2.5 text-sm font-medium hover:bg-zinc-800 inline-flex items-center gap-2" data-testid="create-exam-btn">
            <Plus size={14} /> Create exam
          </Link>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 border border-zinc-200 mb-6" data-testid="exams-stats">
        <Stat label="Total" value={stats.total} />
        <Stat label="Published" value={stats.published} tone="emerald" />
        <Stat label="Draft" value={stats.draft} tone="zinc" />
        <Stat label="Archived" value={stats.archived} tone="amber" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center mb-6">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search exams…"
            className="w-full border border-zinc-300 pl-9 pr-3 py-2 text-sm focus:border-zinc-900 focus:outline-none"
            data-testid="exams-search-input"
          />
        </div>
        {["all", "published", "draft", "archived"].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 text-xs uppercase tracking-wider border ${statusFilter === s ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 text-zinc-700 hover:border-zinc-900"}`}
            data-testid={`exams-status-filter-${s}`}
          >
            {s}
          </button>
        ))}
        {isSuper && institutions.length > 0 && (
          <select
            value={instFilter}
            onChange={(e) => setInstFilter(e.target.value)}
            className="border border-zinc-300 px-3 py-2 text-sm bg-white"
            data-testid="exams-institution-filter"
          >
            <option value="all">All institutions</option>
            {institutions.map((i) => (
              <option key={i.institution_id} value={i.institution_id}>{i.name} ({i.code})</option>
            ))}
          </select>
        )}
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="border border-zinc-300 px-3 py-2 text-sm bg-white"
          data-testid="exams-category-filter"
        >
          <option value="all">All categories</option>
          {examCategories.map((c) => (
            <option key={c.slug} value={c.slug}>{c.label}</option>
          ))}
        </select>
      </div>

      {selected.size > 0 && (
        <div className="mb-4 flex items-center gap-3 border border-zinc-900 bg-zinc-900 text-white px-4 py-3" data-testid="exams-bulk-bar">
          <div className="flex-1 text-sm font-medium">{selected.size} selected</div>
          <button onClick={() => setBulkConfirm("publish")} disabled={bulkBusy} className="text-xs uppercase tracking-wider border border-white/40 px-3 py-1.5 hover:bg-white/10 disabled:opacity-40" data-testid="exams-bulk-publish">Publish</button>
          <button onClick={() => setBulkConfirm("unpublish")} disabled={bulkBusy} className="text-xs uppercase tracking-wider border border-white/40 px-3 py-1.5 hover:bg-white/10 disabled:opacity-40" data-testid="exams-bulk-unpublish">Unpublish</button>
          {isAdmin && (
            <button
              onClick={() => { setRecatCategory(examCategories[0]?.slug || ""); setRecatOpen(true); }}
              disabled={bulkBusy}
              className="text-xs uppercase tracking-wider border border-indigo-400 text-indigo-100 px-3 py-1.5 hover:bg-indigo-500/30 disabled:opacity-40 inline-flex items-center gap-1"
              data-testid="exams-bulk-recategorize"
            >
              <Tag size={12} /> Recategorise
            </button>
          )}
          <button onClick={() => setBulkConfirm("archive")} disabled={bulkBusy} className="text-xs uppercase tracking-wider border border-white/40 px-3 py-1.5 hover:bg-white/10 disabled:opacity-40" data-testid="exams-bulk-archive">Archive</button>
          <button onClick={() => setBulkConfirm("delete")} disabled={bulkBusy} className="text-xs uppercase tracking-wider bg-red-600 border border-red-600 px-3 py-1.5 hover:bg-red-700 disabled:opacity-40" data-testid="exams-bulk-delete">Delete</button>
          <button onClick={clearSelection} className="text-xs uppercase tracking-wider text-white/70 hover:text-white ml-2" data-testid="exams-bulk-clear">Clear</button>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-zinc-200 p-10 text-center text-sm text-zinc-500" data-testid="exams-empty">
          No exams match your filters. Try broadening the search or create a new one.
        </div>
      ) : (
        <div className="border border-zinc-200" data-testid="exams-list">
          {filtered.length > 0 && (
            <div className="flex items-center gap-2 px-5 py-2 border-b border-zinc-200 bg-zinc-50">
              <button onClick={toggleSelectAll} className="text-zinc-700 hover:text-zinc-900" data-testid="exams-select-all" aria-label="Select all">
                {allSelected ? <CheckSquare size={16} weight="fill" /> : <Square size={16} />}
              </button>
              <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Select all ({filtered.length})</span>
            </div>
          )}
          {filtered.map((e, i) => {
            const isSel = selected.has(e.exam_id);
            return (
            <div key={e.exam_id} className={`p-5 flex items-start justify-between gap-4 ${i < filtered.length - 1 ? "border-b border-zinc-200" : ""} ${e.status === "archived" ? "bg-zinc-50/50" : ""} ${isSel ? "bg-blue-50/30" : ""}`} data-testid={`exam-manage-row-${e.exam_id}`}>
              <button onClick={() => toggleSel(e.exam_id)} className="mt-1 text-zinc-600 hover:text-zinc-900 shrink-0" data-testid={`exam-select-${e.exam_id}`} aria-label={isSel ? "Deselect" : "Select"}>
                {isSel ? <CheckSquare size={16} weight="fill" /> : <Square size={16} />}
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <div className="font-medium">{e.title}</div>
                  <span className={`text-[10px] uppercase tracking-wider border px-2 py-0.5 ${STATUS_TONE[e.status] || STATUS_TONE.draft}`} data-testid={`exam-status-${e.exam_id}`}>
                    {e.status}
                  </span>
                  {e.proctoring_enabled === false && (
                    <span className="text-[10px] uppercase tracking-wider border border-zinc-300 text-zinc-600 bg-zinc-50 px-2 py-0.5">
                      No proctoring
                    </span>
                  )}
                  {Number(e.price_credits || 0) > 0 ? (
                    <span className="text-[10px] uppercase tracking-wider border border-amber-300 text-amber-800 bg-amber-50 px-2 py-0.5 inline-flex items-center gap-1" data-testid={`exam-price-badge-${e.exam_id}`}>
                      <Coin size={10} weight="fill" className="text-amber-500" /> <span className="font-mono">{e.price_credits}</span> coins
                    </span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-wider border border-emerald-300 text-emerald-700 bg-emerald-50 px-2 py-0.5" data-testid={`exam-price-badge-${e.exam_id}`}>
                      Free
                    </span>
                  )}
                  <span
                    className="text-[10px] uppercase tracking-wider border border-indigo-300 text-indigo-700 bg-indigo-50 px-2 py-0.5"
                    data-testid={`exam-category-badge-${e.exam_id}`}
                  >
                    {labelFor(e.category)}
                  </span>
                  {isSuper && e.institution_id && (
                    <span className="text-[10px] uppercase tracking-wider border border-blue-300 text-blue-700 bg-blue-50 px-2 py-0.5 inline-flex items-center gap-1" data-testid={`exam-inst-badge-${e.exam_id}`}>
                      <Buildings size={10} /> {instMap[e.institution_id]?.name || e.institution_id}
                    </span>
                  )}
                </div>
                {e.description && <div className="text-xs text-zinc-500 line-clamp-1 mb-2">{e.description}</div>}
                <div className="text-xs text-zinc-500 flex items-center gap-4 flex-wrap">
                  <span className="inline-flex items-center gap-1"><ListChecks size={11} /> {e.question_ids?.length || 0} questions</span>
                  <span className="inline-flex items-center gap-1"><Clock size={11} /> {e.duration_minutes}m</span>
                  <span className="font-mono">{e.total_marks} marks</span>
                  <span className="text-zinc-400 font-mono truncate">{e.exam_id}</span>
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  onClick={() => togglePublish(e)}
                  disabled={busyId === e.exam_id || e.status === "archived"}
                  className={`p-2 border ${e.status === "published" ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : "border-zinc-300 text-zinc-700 hover:bg-zinc-50"} disabled:opacity-40`}
                  data-testid={`exam-toggle-active-${e.exam_id}`}
                  title={e.status === "published" ? "Disable (unpublish)" : "Enable (publish)"}
                >
                  {e.status === "published" ? <ToggleRight size={14} weight="fill" /> : <ToggleLeft size={14} />}
                </button>
                <Link
                  to={`/app/exams-manage/${e.exam_id}`}
                  className="p-2 border border-zinc-300 hover:bg-zinc-50 inline-flex items-center"
                  data-testid={`exam-edit-${e.exam_id}`}
                  title="Edit exam"
                >
                  <PencilSimple size={14} />
                </Link>
                <button
                  onClick={() => setConfirmDelete(e)}
                  className="p-2 border border-red-300 text-red-700 hover:bg-red-50"
                  data-testid={`exam-delete-${e.exam_id}`}
                  title="Delete exam"
                >
                  <Trash size={14} />
                </button>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="exam-delete-confirm">
          <div className="bg-white max-w-md w-full p-6 border border-zinc-200">
            <div className="text-lg font-semibold mb-2">Delete "{confirmDelete.title}"?</div>
            <div className="text-sm text-zinc-600 mb-5">
              If any student has attempted this exam, it will be <strong>archived</strong> to preserve results. Otherwise it will be permanently deleted.
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 border border-zinc-300 text-sm hover:border-zinc-900"
                data-testid="exam-delete-cancel"
              >
                Cancel
              </button>
              <button
                onClick={() => doDelete(confirmDelete)}
                disabled={busyId === confirmDelete.exam_id}
                className="px-4 py-2 bg-red-600 text-white text-sm hover:bg-red-700 disabled:opacity-50"
                data-testid="exam-delete-confirm-btn"
              >
                {busyId === confirmDelete.exam_id ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {bulkConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="exams-bulk-confirm">
          <div className="bg-white max-w-md w-full p-6 border border-zinc-200">
            <div className="text-lg font-semibold mb-2 capitalize">{bulkConfirm} {selected.size} exams?</div>
            <div className="text-sm text-zinc-600 mb-5">
              {bulkConfirm === "delete"
                ? "Exams with student attempts will be archived instead of deleted (audit-safe). Others will be permanently removed."
                : bulkConfirm === "archive"
                  ? "Archived exams stop being visible to students but retain all data."
                  : bulkConfirm === "publish"
                    ? "Published exams become visible to students in your institution."
                    : "Unpublished (draft) exams are hidden from students."}
              <br /><span className="text-xs text-zinc-500">Exams outside your institution are automatically skipped.</span>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setBulkConfirm(null)} className="px-4 py-2 border border-zinc-300 text-sm hover:border-zinc-900" data-testid="exams-bulk-cancel">Cancel</button>
              <button
                onClick={() => runBulk(bulkConfirm)}
                disabled={bulkBusy}
                className={`px-4 py-2 text-white text-sm disabled:opacity-50 ${bulkConfirm === "delete" ? "bg-red-600 hover:bg-red-700" : "bg-zinc-900 hover:bg-zinc-800"}`}
                data-testid="exams-bulk-confirm-btn"
              >
                {bulkBusy ? "Working…" : `${bulkConfirm} ${selected.size}`}
              </button>
            </div>
          </div>
        </div>
      )}
      {recatOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="exams-recategorize-modal">
          <div className="bg-white max-w-md w-full p-6 border border-zinc-200">
            <div className="text-lg font-semibold mb-1">Recategorise {selected.size} exam{selected.size === 1 ? "" : "s"}</div>
            <div className="text-sm text-zinc-600 mb-4">
              Pick the new sector to assign. Only exams from your institution will be updated — others are silently skipped.
            </div>
            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-semibold">New category</div>
              <select
                value={recatCategory}
                onChange={(e) => setRecatCategory(e.target.value)}
                className="w-full border border-zinc-300 px-3 py-2 text-sm bg-white focus:border-zinc-900 focus:outline-none"
                data-testid="exams-recategorize-select"
              >
                {examCategories.map((c) => (
                  <option key={c.slug} value={c.slug}>{c.label}</option>
                ))}
              </select>
            </label>
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setRecatOpen(false)}
                className="px-4 py-2 border border-zinc-300 text-sm hover:border-zinc-900"
                data-testid="exams-recategorize-cancel"
              >Cancel</button>
              <button
                onClick={runRecategorise}
                disabled={bulkBusy || !recatCategory}
                className="px-4 py-2 bg-zinc-900 text-white text-sm hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center gap-2"
                data-testid="exams-recategorize-confirm"
              >
                <Tag size={12} /> {bulkBusy ? "Working…" : `Move ${selected.size} exam${selected.size === 1 ? "" : "s"}`}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

function Stat({ label, value, tone = "zinc" }) {
  const tones = { zinc: "text-zinc-900", emerald: "text-emerald-700", amber: "text-amber-700" };
  return (
    <div className="p-5 border-r border-zinc-200 last:border-r-0">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 font-semibold">{label}</div>
      <div className={`font-display text-2xl font-bold tabular ${tones[tone]}`}>{value}</div>
    </div>
  );
}
